// src/components/TicketPreview.jsx
import { useRef, useState, useEffect, useMemo } from "react";
import { formatSmartDates } from "../../utils/dateFormatter";
import environment from "../../environments/environment";
import { buildGradientBackground } from "./GradientEditor";
import { getTicketHelperLines, getDistanceMeasurements } from "./ticketSnapping";
import "./../../styles/ticket.css";

export default function TicketPreview({
  design,
  data,
  onSectionChange,
  onSectionMeasure,
  selectedId,
  onSelect,
  onHeaderChange,
  hidePairHeading: hidePairHeadingProp = false,
  printableView = false,
  showGrid = false,
}) {
  const effectiveHidePairHeading =
    design?.meta?.hidePairHeading ?? hidePairHeadingProp;
  const containerRef = useRef(null);
  const sectionRefs = useRef({});
  const [dragState, setDragState] = useState(null);
  // dragState: { target: 'header' | 'section', id?, mode: 'move'|'resize', ... }

  const [helperLines, setHelperLines] = useState([]);
  const [distanceBadges, setDistanceBadges] = useState([]);
  const [altPressed, setAltPressed] = useState(false);

  const { canvas, header, sectionsOrder, sections } = design || {
    canvas: {},
    header: {},
    sectionsOrder: [],
    sections: {},
  };

  const getElementTypeLabel = (id, sec) => {
    if (id === "header") return "Header";
    if (
      id === "qrData" ||
      sec?.imageKey === "qrData" ||
      sec?.fieldBinding?.fieldName === "QR Code" ||
      sec?.fieldBinding?.fieldKey === "qrData"
    )
      return "QR Code";
    if (id === "profileImage" || sec?.imageKey === "profileImage")
      return "Profile Image";
    if (sec?.fieldBinding?.fieldName) return sec.fieldBinding.fieldName;
    if (sec?.label) return sec.label;
    if (sec?.heading) return sec.heading;
    if (id === "badgeTicketClass" || id === "ticketClass")
      return "Ticket Class";
    if (id === "firstName" || id === "fullName") return "First Name";
    if (id === "lastName") return "Last Name";
    if (id === "company") return "Company";
    if (sec?.type === "pill") return "Badge Pill";
    if (sec?.type === "divider") return "Divider";
    if (sec?.type === "shape") return "Shape";
    if (sec?.type === "image") return "Image";
    return sec?.type
      ? sec.type.charAt(0).toUpperCase() + sec.type.slice(1)
      : "Element";
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Alt") setAltPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Alt") setAltPressed(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const measureSection = (id, node) => {
    if (node) {
      sectionRefs.current[id] = node;
    }
    if (!node || !onSectionMeasure) return;

    const width = Math.round(node.offsetWidth || 0);
    const height = Math.round(node.offsetHeight || 0);
    if (width > 0 || height > 0) {
      onSectionMeasure(id, { width, height });
    }
  };

  useEffect(() => {
    if (!design || !selectedId) return;

    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input or textarea
      const isTyping = ["INPUT", "TEXTAREA"].includes(
        document.activeElement?.tagName,
      );
      if (isTyping) return;

      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;

      switch (e.key) {
        case "ArrowLeft":
          dx = -step;
          break;
        case "ArrowRight":
          dx = step;
          break;
        case "ArrowUp":
          dy = -step;
          break;
        case "ArrowDown":
          dy = step;
          break;
        default:
          return; // Not an arrow key
      }

      // Prevent page scrolling when using arrow keys to move elements
      e.preventDefault();

      if (selectedId === "header") {
        if (!onHeaderChange) return;
        onHeaderChange({
          x: (header.x || 0) + dx,
          y: (header.y || 0) + dy,
        });
      } else {
        const sec = sections[selectedId];
        if (!sec || !onSectionChange) return;
        onSectionChange(selectedId, {
          x: (sec.x || 0) + dx,
          y: (sec.y || 0) + dy,
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [design, selectedId, sections, header, onSectionChange, onHeaderChange]);

  const activeDistanceBadges = useMemo(() => {
    if (dragState && dragState.mode === "move") {
      return distanceBadges;
    }
    if (altPressed && selectedId && containerRef.current && design) {
      const canvasW =
        containerRef.current.clientWidth || Number(canvas?.width) || 400;
      const canvasH =
        containerRef.current.clientHeight || Number(canvas?.height) || 600;
      const sec = sections[selectedId];
      if (!sec) return [];
      const w = sec.width || sectionRefs.current[selectedId]?.offsetWidth || 80;
      const h =
        sec.height || sectionRefs.current[selectedId]?.offsetHeight || 30;
      const activeNode = {
        id: selectedId,
        x: sec.x || 0,
        y: sec.y || 0,
        width: w,
        height: h,
      };

      const siblings = [];
      if (header?.enabled) {
        siblings.push({
          id: "header",
          x: header.x || 0,
          y: header.y || 0,
          width: canvasW,
          height: header.height || 40,
        });
      }
      for (const [sId, sSec] of Object.entries(sections || {})) {
        if (sId !== selectedId && sSec && sSec.enabled) {
          siblings.push({
            id: sId,
            x: sSec.x || 0,
            y: sSec.y || 0,
            width: sSec.width || sectionRefs.current[sId]?.offsetWidth || 80,
            height: sSec.height || sectionRefs.current[sId]?.offsetHeight || 30,
          });
        }
      }
      return getDistanceMeasurements(activeNode, siblings, {
        width: canvasW,
        height: canvasH,
      });
    }
    return [];
  }, [dragState, distanceBadges, altPressed, selectedId, sections, header, design]);

  if (!design) return null;

  const getValueFromPath = (obj, path) => {
    if (!path) return "";
    return path.split(".").reduce((acc, k) => (acc ? acc[k] : ""), obj);
  };

  const normalizeFieldName = (value) =>
    String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

  const getObjectFieldValue = (source, names) => {
    if (!source || typeof source !== "object") return "";

    for (const name of names) {
      if (!name) continue;
      if (source[name] !== undefined && source[name] !== null) {
        return source[name];
      }
    }

    const lookup = new Map(
      Object.entries(source).map(([key, value]) => [normalizeFieldName(key), value]),
    );
    for (const name of names) {
      const value = lookup.get(normalizeFieldName(name));
      if (value !== undefined && value !== null) return value;
    }

    return "";
  };

  const getFormFieldValue = (sec) => {
    const binding = sec.fieldBinding || {};
    const names = [
      binding.fieldName,
      binding.fieldKey,
      sec.fieldName,
      sec.fieldKey,
      sec.label,
    ].filter(Boolean);

    const value = [
      getObjectFieldValue(data?.fields, names),
      getObjectFieldValue(data?.formData, names),
      getObjectFieldValue(data, names),
    ].find((item) => item !== "" && item !== undefined && item !== null);

    if (Array.isArray(value)) return value.join(", ");
    if (value && typeof value === "object") return JSON.stringify(value);
    return value === undefined || value === null ? "" : String(value);
  };

  const getTicketNameValue = (sec) => {
    if (sec.isFormField || sec.fieldBinding) {
      return getFormFieldValue(sec);
    }
    if (sec.keyPathFallbacks && Array.isArray(sec.keyPathFallbacks)) {
      for (const k of sec.keyPathFallbacks) {
        const v = getValueFromPath(data, k);
        if (v) return v;
      }
    }
    if (sec.keyPath) {
      const v = getValueFromPath(data, sec.keyPath);
      if (v) return v;
    }
    const formVal = getFormFieldValue(sec);
    if (formVal) return formVal;
    return sec.text || "";
  };

  const hasDateBinding = (id, sec) => {
    const fallbackPaths = Array.isArray(sec.keyPathFallbacks) ? sec.keyPathFallbacks : [];
    return id === "date" || sec.keyPath === "date" || fallbackPaths.includes("date");
  };

  const getMinDimensions = (sec) => {
    if (!sec) return { minWidth: 10, minHeight: 10 };
    // QR Code
    if (sec.imageKey === "qrData") return { minWidth: 40, minHeight: 40 };
    // Profile Image
    if (sec.imageKey === "profileImage") return { minWidth: 20, minHeight: 20 };
    // Standard Types
    if (sec.type === "image") return { minWidth: 10, minHeight: 10 };
    if (sec.type === "shape") return { minWidth: 10, minHeight: 10 };
    if (sec.type === "pill") return { minWidth: 10, minHeight: 18 };
    if (sec.type === "divider") return { minWidth: 10, minHeight: 1 };
    // Text based layers should resize smoothly without snapping to old defaults.
    if (sec.type === "text" || sec.type === "pair")
      return { minWidth: 10, minHeight: 10 };
    return { minWidth: 10, minHeight: 10 };
  };

  const getLayerBorderRadius = (sec, fallback = 0) => {
    const base = Number(sec?.borderRadius ?? fallback) || 0;
    const topLeft = Number(sec?.borderTopLeftRadius ?? base) || 0;
    const topRight = Number(sec?.borderTopRightRadius ?? base) || 0;
    const bottomRight = Number(sec?.borderBottomRightRadius ?? base) || 0;
    const bottomLeft = Number(sec?.borderBottomLeftRadius ?? base) || 0;
    return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
  };

  const getCanvasScale = () => {
    if (!containerRef.current) return 1;
    const rect = containerRef.current.getBoundingClientRect();
    const clientW = containerRef.current.clientWidth || 1;
    return rect.width > 0 && clientW > 0 ? rect.width / clientW : 1;
  };

  const getCanvasPoint = (e) => {
    if (!containerRef.current) return { x: 0, y: 0, scaleX: 1, scaleY: 1 };
    const rect = containerRef.current.getBoundingClientRect();
    const clientW = containerRef.current.clientWidth || 1;
    const clientH = containerRef.current.clientHeight || 1;
    const scaleX = rect.width > 0 && clientW > 0 ? rect.width / clientW : 1;
    const scaleY = rect.height > 0 && clientH > 0 ? rect.height / clientH : 1;
    return {
      x: (e.clientX - rect.left) / scaleX,
      y: (e.clientY - rect.top) / scaleY,
      scaleX,
      scaleY,
    };
  };

  const startMoveHeader = (e) => {
    if (!containerRef.current || !onHeaderChange) return;
    e.stopPropagation();

    const { x: mouseX, y: mouseY } = getCanvasPoint(e);
    const x = header.x || 0;
    const y = header.y || 0;

    setDragState({
      target: "header",
      mode: "move",
      offsetX: mouseX - x,
      offsetY: mouseY - y,
    });

    onSelect && onSelect("header");
  };

  const startMoveSection = (id, sec, e) => {
    if (!containerRef.current || !onSectionChange) return;
    // Don't start drag if clicking on a resize handle
    if (e.target.closest("[data-resize-handle]")) return;

    e.stopPropagation();
    e.preventDefault();

    const { x: mouseX, y: mouseY } = getCanvasPoint(e);
    const x = sec.x || 0;
    const y = sec.y || 0;

    // Get actual rendered dimensions from the element (unscaled DOM offset dimensions)
    const element = e.currentTarget;
    const actualWidth = sec.width || element.offsetWidth || 80;
    const actualHeight = sec.height || element.offsetHeight || 30;

    setDragState({
      target: "section",
      id,
      mode: "move",
      offsetX: mouseX - x,
      offsetY: mouseY - y,
      sectionWidth: actualWidth,
      sectionHeight: actualHeight,
    });

    onSelect && onSelect(id);
  };

  const startResizeSection = (id, sec, e, direction = "se") => {
    if (!containerRef.current || !onSectionChange) return;
    e.stopPropagation();
    e.preventDefault();

    const { x: mouseX, y: mouseY } = getCanvasPoint(e);
    const renderedElement = e.currentTarget.parentElement;
    const renderedWidth = sec.width || renderedElement?.offsetWidth || 80;
    const renderedHeight = sec.height || renderedElement?.offsetHeight || 80;

    setDragState({
      target: "section",
      id,
      mode: "resize",
      direction,
      startX: mouseX,
      startY: mouseY,
      startLeft: sec.x || 0,
      startTop: sec.y || 0,
      startW: renderedWidth,
      startH: renderedHeight,
      keepSquare: sec.keepSquare,
    });

    onSelect && onSelect(id);
  };

  const handleMouseMove = (e) => {
    if (!dragState || !containerRef.current) return;

    e.preventDefault();

    const { x: mouseX, y: mouseY } = getCanvasPoint(e);
    const canvasW =
      containerRef.current.clientWidth || Number(canvas?.width) || 400;
    const canvasH =
      containerRef.current.clientHeight || Number(canvas?.height) || 600;
    const scale = getCanvasScale();
    const effectiveThreshold = Math.max(8, Math.round(12 / (scale || 1)));

    // MOVE HEADER
    if (dragState.target === "header" && dragState.mode === "move") {
      let x = mouseX - dragState.offsetX;
      let y = mouseY - dragState.offsetY;

      const headerHeight = header.height || 40;
      const siblings = [];
      for (const [sId, sSec] of Object.entries(sections)) {
        if (sSec && sSec.enabled) {
          siblings.push({
            id: sId,
            x: sSec.x || 0,
            y: sSec.y || 0,
            width: sSec.width || sectionRefs.current[sId]?.offsetWidth || 80,
            height: sSec.height || sectionRefs.current[sId]?.offsetHeight || 30,
          });
        }
      }

      const snapResult = getTicketHelperLines(
        { id: "header", x, y, width: canvasW, height: headerHeight },
        siblings,
        { width: canvasW, height: canvasH },
        effectiveThreshold,
      );

      if (snapResult.snapX !== null) x = snapResult.snapX;
      if (snapResult.snapY !== null) y = snapResult.snapY;

      setHelperLines(snapResult.lines || []);

      const maxX = canvasW - 10;
      const maxY = canvasH - headerHeight;
      if (x < 0) x = 0;
      if (y < 0) y = 0;
      if (x > maxX) x = maxX;
      if (y > maxY) y = maxY;

      onHeaderChange && onHeaderChange({ x, y });
      return;
    }

    // MOVE SECTION
    if (dragState.target === "section" && dragState.mode === "move") {
      let x = mouseX - dragState.offsetX;
      let y = mouseY - dragState.offsetY;

      const w = dragState.sectionWidth || 10;
      const h = dragState.sectionHeight || 10;

      // Build sibling nodes list for snapping & measurements
      const siblings = [];
      if (header?.enabled) {
        siblings.push({
          id: "header",
          x: header.x || 0,
          y: header.y || 0,
          width: canvasW,
          height: header.height || 40,
        });
      }
      for (const [sId, sSec] of Object.entries(sections)) {
        if (sId !== dragState.id && sSec && sSec.enabled) {
          siblings.push({
            id: sId,
            x: sSec.x || 0,
            y: sSec.y || 0,
            width: sSec.width || sectionRefs.current[sId]?.offsetWidth || 80,
            height: sSec.height || sectionRefs.current[sId]?.offsetHeight || 30,
          });
        }
      }

      // Magnetic Snapping & Helper Lines
      const snapResult = getTicketHelperLines(
        { id: dragState.id, x, y, width: w, height: h },
        siblings,
        { width: canvasW, height: canvasH },
        effectiveThreshold,
      );

      if (snapResult.snapX !== null) {
        x = snapResult.snapX;
      }
      if (snapResult.snapY !== null) {
        y = snapResult.snapY;
      }

      setHelperLines(snapResult.lines || []);

      // Real-time Distance Measurements
      const dists = getDistanceMeasurements(
        { id: dragState.id, x, y, width: w, height: h },
        siblings,
        { width: canvasW, height: canvasH },
      );
      setDistanceBadges(dists || []);

      // Allow free movement - only constrain if element would go completely outside
      const maxX = canvasW - Math.min(w, 50);
      const maxY = canvasH - Math.min(h, 50);

      if (x < -w + 20) x = -w + 20;
      if (y < -h + 20) y = -h + 20;
      if (x > maxX) x = maxX;
      if (y > maxY) y = maxY;

      onSectionChange && onSectionChange(dragState.id, { x, y });
      return;
    }

    // RESIZE SECTION (from any side / corner)
    if (dragState.target === "section" && dragState.mode === "resize") {
      const dx = mouseX - dragState.startX;
      const dy = mouseY - dragState.startY;
      const { direction, startW, startH, startLeft, startTop, keepSquare } =
        dragState;

      let newLeft = startLeft;
      let newTop = startTop;
      let newW = startW;
      let newH = startH;

      // Determine if it's a corner or side resize
      const isCorner = direction.length === 2; // nw, ne, sw, se
      const isSide = direction.length === 1; // n, s, w, e

      // Handle horizontal resize (east/west)
      if (direction.includes("e")) {
        newW = startW + dx;
      } else if (direction.includes("w")) {
        newW = startW - dx;
        newLeft = startLeft + dx;
      }

      // Handle vertical resize (south/north)
      if (direction.includes("s")) {
        newH = startH + dy;
      } else if (direction.includes("n")) {
        newH = startH - dy;
        newTop = startTop + dy;
      }

      // For pure side resizes, lock the other dimension
      if (isSide) {
        if (direction === "e" || direction === "w") {
          newH = startH;
        }
        if (direction === "n" || direction === "s") {
          newW = startW;
        }
      }

      // Keep minimum size
      const policy = getMinDimensions(sections[dragState.id]);
      newW = Math.max(policy.minWidth, newW);
      if (
        direction === "se" ||
        direction === "e" ||
        direction === "w" ||
        direction === "ne" ||
        direction === "sw" ||
        direction === "nw"
      ) {
        newH = Math.max(policy.minHeight, newH);
      }

      // Maintain square if requested (used for QR/profile)
      if (keepSquare) {
        const size = Math.max(newW, newH);
        if (direction.includes("w")) {
          newLeft = startLeft + (startW - size);
        }
        if (direction.includes("n")) {
          newTop = startTop + (startH - size);
        }
        newW = size;
        newH = size;
      }

      // Clamp within container bounds
      const maxX = canvasW - newW;
      const maxY = canvasH - newH;

      if (newLeft < 0) {
        if (direction.includes("w")) {
          newW = newW + newLeft;
          newLeft = 0;
        } else {
          newLeft = 0;
        }
      }
      if (newTop < 0) {
        if (direction.includes("n")) {
          newH = newH + newTop;
          newTop = 0;
        } else {
          newTop = 0;
        }
      }
      if (newLeft > maxX) {
        newLeft = maxX;
        if (direction.includes("e")) {
          newW = canvasW - newLeft;
        }
      }
      if (newTop > maxY) {
        newTop = maxY;
        if (direction.includes("s")) {
          newH = canvasH - newTop;
        }
      }

      // Ensure minimum size after bounds adjustment
      const finalPolicy = getMinDimensions(sections[dragState.id]);
      newW = Math.max(finalPolicy.minWidth, newW);
      newH = Math.max(finalPolicy.minHeight, newH);

      onSectionChange &&
        onSectionChange(dragState.id, {
          x: newLeft,
          y: newTop,
          width: newW,
          height: newH,
        });
    }
  };

  const endDrag = (e) => {
    if (dragState) {
      setDragState(null);
      setHelperLines([]);
      setDistanceBadges([]);
    }
  };

  const renderContent = (id, sec) => {
    if (sec.type === "text") {
      let content = getTicketNameValue(sec);

      if (!content && (sec.isFormField || sec.fieldBinding)) {
        content = sec.previewText || sec.fieldBinding?.fieldName || sec.label || "";
      }

      // Apply smart formatting for validity dates
      if (id === "ticketValidDates" || sec.keyPath === "ticketValidDates") {
        content = formatSmartDates(content);
      } else if (hasDateBinding(id, sec) && data?.ticketValidDates) {
        content = formatSmartDates(data.ticketValidDates);
      }

      if ((id === "venue" || sec.keyPath === "venue") && !content) {
        content = "Indira Gandhi National Auditorium";
      }

      if (
        (id === "companyName" ||
          id === "company" ||
          sec.keyPath === "company_name" ||
          sec.keyPath === "companyName" ||
          sec.keyPath === "company" ||
          (Array.isArray(sec.keyPathFallbacks) &&
            sec.keyPathFallbacks.some((k) => /company/i.test(k))) ||
          /company/i.test(sec.label || "")) &&
        !content
      ) {
        content = "Acme Innovations Ltd.";
      }

      if (!content && (sec.keyPath || sec.keyPathFallbacks) && id !== "footer")
        return null;

      const isLeftAligned = sec.align === "left";

      return (
        <div
          style={{
            fontFamily: sec.fontFamily,
            fontSize: sec.fontSize,
            fontWeight: sec.fontWeight,
            lineHeight: `${Number(sec.fontSize || 14) * 1.2 + Number(sec.lineSpacing || 0)}px`,
            wordSpacing: Number(sec.wordSpacing || 0),
            color: sec.color,
            textAlign: sec.align,
            width: sec.width || "auto",
            minHeight: sec.height || undefined,
            letterSpacing: Number(sec.letterSpacing || 0),
            display: "flex",
            flexDirection: "column",
            justifyContent:
              sec.verticalAlign === "top"
                ? "flex-start"
                : sec.verticalAlign === "bottom"
                  ? "flex-end"
                  : "center",
            alignItems:
              sec.align === "left"
                ? "flex-start"
                : sec.align === "right"
                  ? "flex-end"
                  : "center",
            userSelect: "none",
            pointerEvents: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {content}
        </div>
      );
    }

    if (sec.type === "pair") {
      const value = getValueFromPath(data, sec.keyPath) || "";
      if (!value && !sec.showWhenEmpty) return null;

      // heading text: use the custom override if set, otherwise fall back to the section label
      const headingText = effectiveHidePairHeading
        ? ""
        : sec.heading || sec.label || "";

      return (
        <div
          style={{
            textAlign: sec.align,
            width: sec.width || "auto",
            minHeight: sec.height || undefined,
            letterSpacing: Number(sec.letterSpacing || 0),
            display: "flex",
            flexDirection: "column",
            justifyContent:
              sec.verticalAlign === "top"
                ? "flex-start"
                : sec.verticalAlign === "bottom"
                  ? "flex-end"
                  : "center",
            alignItems:
              sec.align === "left"
                ? "flex-start"
                : sec.align === "right"
                  ? "flex-end"
                  : "center",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {headingText ? (
            <div
              style={{
                fontSize: sec.headingFontSize,
                color: sec.color,
                opacity: 0.7,
              }}
            >
              {headingText}
            </div>
          ) : null}
          <div
            style={{
              fontSize: sec.valueFontSize,
              color: sec.color,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </div>
        </div>
      );
    }

    if (sec.type === "pill") {
      const value = getTicketNameValue(sec);
      if (!value) return null;

      const isLeftAligned = sec.align === "left";
      const pillBackground =
        (sec.fillMode || (sec.gradientStops ? "gradient" : "solid")) ===
        "gradient"
          ? buildGradientBackground({
              mode: "gradient",
              angle: Number(sec.gradientAngle ?? 135),
              stops:
                sec.gradientStops || [
                  {
                    id: `${id}-pill-stop-1`,
                    position: 0,
                    color: sec.backgroundGradientFrom || sec.bg || "#4f46e5",
                    opacity: 100,
                  },
                  {
                    id: `${id}-pill-stop-2`,
                    position: 100,
                    color: sec.backgroundGradientTo || "#a855f7",
                    opacity: 100,
                  },
                ],
              fallback: [sec.bg || "#4f46e5", "#a855f7"],
            })
          : sec.bg;

      return (
        <div
          style={{
            textAlign: sec.align,
            width: isLeftAligned ? "auto" : sec.width || "auto",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: pillBackground,
              color: sec.textColor,
              display: sec.fullWidth ? "block" : "inline-block",
              width: sec.fullWidth ? "100%" : "auto",
              minWidth: sec.fullWidth ? "100%" : "60%",
              padding: "3px 12px",
              borderRadius: getLayerBorderRadius(sec, 6),
              minHeight: sec.height,
              lineHeight: `1.2`,
              fontWeight: sec.fontWeight,
              fontSize: sec.fontSize,
              letterSpacing: Number(sec.letterSpacing || 0),
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              display: "flex",
              alignItems: "center",
              justifyContent:
                sec.align === "left"
                  ? "flex-start"
                  : sec.align === "right"
                    ? "flex-end"
                    : "center",
            }}
          >
            {value}
          </div>
        </div>
      );
    }

    if (sec.type === "image") {
      let src = sec.customImage || (sec.imageKey ? data[sec.imageKey] : null);
      if (!src && sec.imageKey === "profileImage") {
        src = environment.defaultTicketImageUrl || null;
      }
      const w = sec.width || 80;
      const h = sec.height || 80;
      const isQrImage = sec.imageKey === "qrData";
      const qrBorderWidth = Math.max(0, Number(sec.qrBorderWidth) || 0);
      const qrBorderGap = Math.max(0, Number(sec.qrBorderGap) || 0);
      const qrInnerInset = isQrImage ? qrBorderWidth + qrBorderGap : 0;
      const defaultRadius = sec.imageKey === "qrData" ? 0 : 8;
      const borderRadius = sec.asCircle
        ? "50%"
        : getLayerBorderRadius(sec, defaultRadius);

      return (
        <div
          style={{
            width: w,
            height: h,
            background: isQrImage ? "#ffffff" : "#f2f2f2",
            border: isQrImage
              ? `${qrBorderWidth}px solid ${sec.qrBorderColor || "#000000"}`
              : undefined,
            padding: isQrImage ? qrBorderGap : 0,
            boxSizing: "border-box",
            borderRadius,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {src ? (
            <img
              alt={sec.label || "image"}
              src={src}
              style={{
                width: qrInnerInset * 2 < w ? "100%" : 0,
                height: qrInnerInset * 2 < h ? "100%" : 0,
                objectFit: isQrImage ? "contain" : "cover",
                pointerEvents: "none",
                userSelect: "none",
              }}
              draggable={false}
            />
          ) : (
            <span
              style={{
                fontSize: 12,
                color: "#999",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              {sec.imageKey === "qrData" ? "QR" : "IMG"}
            </span>
          )}
        </div>
      );
    }

    if (sec.type === "divider") {
      return (
        <div
          style={{
            height: sec.thickness,
            background: sec.color,
            width: sec.width || "100%",
            opacity: 0.8,
          }}
        />
      );
    }

    if (sec.type === "shape") {
      const isCircle = sec.shapeType === "circle";
      const w = sec.width || 100;
      const h = sec.height || 100;
      const shapeBackground =
        (sec.fillMode || (sec.gradientStops ? "gradient" : "solid")) ===
        "gradient"
          ? buildGradientBackground({
              mode: "gradient",
              angle: Number(sec.gradientAngle ?? 135),
              stops:
                sec.gradientStops || [
                  {
                    id: `${id}-shape-stop-1`,
                    position: 0,
                    color:
                      sec.backgroundGradientFrom ||
                      sec.backgroundColor ||
                      "#cccccc",
                    opacity: 100,
                  },
                  {
                    id: `${id}-shape-stop-2`,
                    position: 100,
                    color: sec.backgroundGradientTo || "#a855f7",
                    opacity: 100,
                  },
                ],
              fallback: [sec.backgroundColor || "#cccccc", "#a855f7"],
            })
          : sec.backgroundColor || "#cccccc";

      return (
        <div
          style={{
            width: w,
            height: h,
            background: shapeBackground,
            border: `${sec.borderWidth || 0}px solid ${sec.borderColor || "#000000"}`,
            borderRadius: isCircle ? "50%" : getLayerBorderRadius(sec, 0),
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      );
    }

    return null;
  };

  const padding = canvas.padding ?? 20;
  const backgroundImage =
    printableView && canvas.printBackgroundImage === false
      ? ""
      : canvas.backgroundImage || "";
  const backgroundImageOpacity = canvas.backgroundImageOpacity ?? 0.5;
  const fallbackCanvasRadius = Number(canvas.borderRadius ?? 20) || 0;
  const canvasTopLeftRadius =
    Number(canvas.borderTopLeftRadius ?? fallbackCanvasRadius) || 0;
  const canvasTopRightRadius =
    Number(canvas.borderTopRightRadius ?? fallbackCanvasRadius) || 0;
  const canvasBottomRightRadius =
    Number(canvas.borderBottomRightRadius ?? fallbackCanvasRadius) || 0;
  const canvasBottomLeftRadius =
    Number(canvas.borderBottomLeftRadius ?? fallbackCanvasRadius) || 0;
  const canvasBorderRadius = `${canvasTopLeftRadius}px ${canvasTopRightRadius}px ${canvasBottomRightRadius}px ${canvasBottomLeftRadius}px`;
  const canvasBackgroundMode =
    canvas.backgroundMode ||
    (canvas.backgroundGradientFrom || canvas.backgroundGradientTo
      ? "gradient"
      : "solid");
  const canvasBackgroundFill =
    canvasBackgroundMode === "gradient"
      ? `linear-gradient(${Number(canvas.backgroundGradientAngle ?? 135)}deg, ${
          canvas.backgroundGradientFrom || canvas.background || "#ffffff"
        }, ${canvas.backgroundGradientTo || "#dbeafe"})`
      : canvas.background;
  const parsedCanvasBorderWidth = Number(canvas.borderWidth);
  const canvasBorderWidth =
    Number.isFinite(parsedCanvasBorderWidth) && parsedCanvasBorderWidth >= 0
      ? parsedCanvasBorderWidth
      : 1;
  const canvasBorderColor = canvas.borderColor || "#000000";

  return (
    <div
      className="ticket-root"
      style={{
        position: "relative",
        width: canvas.width,
        height: canvas.height,
        padding,
        borderRadius: canvasBorderRadius,
        border: `${canvasBorderWidth}px solid ${canvasBorderColor}`,
        background: canvasBackgroundFill,
        overflow: "hidden",
      }}
    >
      {/* Background Image Layer */}
      {backgroundImage && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: backgroundImageOpacity,
            borderRadius: canvasBorderRadius,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
      )}
      <div
        className="ticket-body"
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 1,
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onMouseDown={(e) => {
          if (
            e.target === containerRef.current ||
            e.target === e.currentTarget
          ) {
            onSelect && onSelect(null);
          }
        }}
      >
        {/* Confined Canvas Grid (10 small boxes inside 1 darker big box - matches Zoho Backstage) */}
        {showGrid && !printableView && (
          <svg
            className="pointer-events-none absolute inset-0 z-0"
            width="100%"
            height="100%"
            shapeRendering="crispEdges"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              {/* Minor grid: 10px small box */}
              <pattern
                id="ticket-grid-small"
                width="10"
                height="10"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 10 0 L 0 0 0 10"
                  fill="none"
                  stroke="#CBD5E1"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              </pattern>
              {/* Major grid: 100px big box (contains exactly 10 small boxes) with darker border */}
              <pattern
                id="ticket-grid-major"
                width="100"
                height="100"
                patternUnits="userSpaceOnUse"
              >
                <rect width="100" height="100" fill="url(#ticket-grid-small)" />
                <path
                  d="M 100 0 L 0 0 0 100"
                  fill="none"
                  stroke="#94A3B8"
                  strokeWidth="1.25"
                  vectorEffect="non-scaling-stroke"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ticket-grid-major)" />
          </svg>
        )}

        {/* Header as draggable block */}
        {header.enabled && (
          <div
            className="ticket-header"
            style={{
              position: "absolute",
              left: header.x || 0,
              top: header.y || 0,
              width: "100%",
              height: header.height,
              background: header.bg,
              color: header.color,
              fontFamily: header.fontFamily,
              fontWeight: header.fontWeight,
              fontSize: header.fontSize,
              textAlign: header.align,
              display: "flex",
              alignItems: "center",
              justifyContent:
                header.align === "left"
                  ? "flex-start"
                  : header.align === "right"
                    ? "flex-end"
                    : "center",
              padding: "0 10px",
              borderTopLeftRadius: Math.max(canvasTopLeftRadius - 1, 0),
              borderTopRightRadius: Math.max(canvasTopRightRadius - 1, 0),
              cursor: "move",
              outline: selectedId === "header" ? "1px dashed #00b4d8" : "none",
              outlineOffset: selectedId === "header" ? "1px" : "0px",
              zIndex: selectedId === "header" ? 25 : 5,
            }}
            onMouseDown={startMoveHeader}
          >
            {selectedId === "header" && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: -18,
                  background: "#3b82f6",
                  color: "#ffffff",
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "1px 5px",
                  borderRadius: 3,
                  pointerEvents: "none",
                  userSelect: "none",
                  zIndex: 35,
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                  letterSpacing: "0.01em",
                  lineHeight: "12px",
                }}
              >
                Header
              </div>
            )}
            {header.text}
          </div>
        )}

        {/* Sections */}
        {[...sectionsOrder]
          .reverse()
          .filter((id) => id !== "email")
          .map((id) => {
            const sec = sections[id];
            if (!sec || !sec.enabled) return null;
            const content = renderContent(id, sec);
            if (!content) return null;

            const isSelected = selectedId === id;

            // Make text/pair/pill/image sections resizable by default unless explicitly disabled
            const isResizeCapableType =
              sec.type === "image" ||
              sec.type === "text" ||
              sec.type === "pair" ||
              sec.type === "pill" ||
              sec.type === "shape";
            const isResizable = isResizeCapableType
              ? sec.isResizable !== false
              : !!sec.isResizable;

            return (
              <div
                key={id}
                ref={(node) => measureSection(id, node)}
                style={{
                  position: "absolute",
                  left: sec.x || 0,
                  top: sec.y || 0,
                  cursor: isSelected && isResizable ? "default" : "move",
                  outline: isSelected ? "1px dashed #00b4d8" : "none",
                  outlineOffset: isSelected ? "1px" : "0px",
                  zIndex: isSelected ? 20 : 1,
                  userSelect: "none",
                  transform: `rotate(${sec.rotation || 0}deg)`,
                  transformOrigin: "center center",
                }}
                onMouseDown={(e) => startMoveSection(id, sec, e)}
              >
                {/* Left-aligned compact Element Type Badge */}
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: -18,
                      background: "#3b82f6",
                      color: "#ffffff",
                      fontSize: 9,
                      fontWeight: 600,
                      padding: "1px 5px",
                      borderRadius: 3,
                      pointerEvents: "none",
                      userSelect: "none",
                      zIndex: 35,
                      whiteSpace: "nowrap",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                      letterSpacing: "0.01em",
                      lineHeight: "12px",
                    }}
                  >
                    {getElementTypeLabel(id, sec)}
                  </div>
                )}
                <div
                  style={{
                    pointerEvents: isSelected && isResizable ? "auto" : "auto",
                  }}
                >
                  {content}
                </div>
                {isSelected && isResizable && (
                  <>
                    {/* Top rotation handle */}
                    <div
                      title="Rotate"
                      style={{
                        position: "absolute",
                        top: -38,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "grab",
                        zIndex: 36,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                      }}
                    >
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#00b4d8"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.85.83 6.72 2.24L21 8" />
                        <path d="M21 3v5h-5" />
                      </svg>
                    </div>

                    {/* Corner handles */}
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "nw")}
                      style={{
                        position: "absolute",
                        left: -4,
                        top: -4,
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "nwse-resize",
                        zIndex: 30,
                      }}
                    />
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "ne")}
                      style={{
                        position: "absolute",
                        right: -4,
                        top: -4,
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "nesw-resize",
                        zIndex: 30,
                      }}
                    />
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "sw")}
                      style={{
                        position: "absolute",
                        left: -4,
                        bottom: -4,
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "nesw-resize",
                        zIndex: 30,
                      }}
                    />
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "se")}
                      style={{
                        position: "absolute",
                        right: -4,
                        bottom: -4,
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "nwse-resize",
                        zIndex: 30,
                      }}
                    />
                    {/* Side handles */}
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "n")}
                      style={{
                        position: "absolute",
                        top: -4,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "ns-resize",
                        zIndex: 30,
                      }}
                    />
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "s")}
                      style={{
                        position: "absolute",
                        bottom: -4,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "ns-resize",
                        zIndex: 30,
                      }}
                    />
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "w")}
                      style={{
                        position: "absolute",
                        left: -4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "ew-resize",
                        zIndex: 30,
                      }}
                    />
                    <div
                      data-resize-handle
                      onMouseDown={(e) => startResizeSection(id, sec, e, "e")}
                      style={{
                        position: "absolute",
                        right: -4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 7,
                        height: 7,
                        background: "#ffffff",
                        border: "1.5px solid #00b4d8",
                        borderRadius: 1,
                        cursor: "ew-resize",
                        zIndex: 30,
                      }}
                    />
                  </>
                )}
              </div>
            );
          })}

        {/* Smart Alignment Guides (Solid Cyan Lines - Left, Center, Right & Top, Center, Bottom) */}
        {helperLines.length > 0 && (
          <svg
            className="pointer-events-none absolute inset-0 z-40 w-full h-full"
            style={{ overflow: "hidden" }}
          >
            {helperLines.map((line, idx) => (
              <line
                key={`guide-${idx}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="#00b4d8"
                strokeWidth={line.isAligned ? "1.75" : "1"}
                vectorEffect="non-scaling-stroke"
                opacity={line.isAligned ? 1 : 0.75}
              />
            ))}
          </svg>
        )}

        {/* Distance Measurements (Clean Red Text like Zoho Backstage) */}
        {activeDistanceBadges.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-45 overflow-hidden">
            {activeDistanceBadges.map((badge, idx) => {
              const currentScale = getCanvasScale();
              const invScale = currentScale > 0 ? 1 / currentScale : 1;
              return (
                <div
                  key={`dist-badge-${idx}`}
                  style={{
                    position: "absolute",
                    left: badge.x,
                    top: badge.y,
                    transform: `translate(-50%, -50%) scale(${invScale})`,
                    transformOrigin: "center center",
                    color: "#ef4444",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    lineHeight: 1,
                    userSelect: "none",
                    pointerEvents: "none",
                    textShadow:
                      "0 0 2px #ffffff, 0 0 3px #ffffff, 0 0 4px #ffffff",
                  }}
                >
                  {badge.value}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
